/**
 * UploadDocumentStep.js
 *
 * Step 2: Upload the document (PDF, image, or Word doc).
 */

import React, { useRef, useState } from 'react';
import { Upload, FileText, Image, File, X, AlertCircle } from 'lucide-react';
import {
  SUPPORTED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_BYTES,
  CHECKLIST_TYPE_CONFIG
} from '../../../../../../config/operations/checklistImportConfig';

const UploadDocumentStep = ({ file, onFileSelect, checklistType, error }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState(null);

  const typeConfig = CHECKLIST_TYPE_CONFIG[checklistType];

  // File type icon
  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return Image;
    if (mimeType === 'application/pdf') return FileText;
    return File;
  };

  // Validate file
  const validateFile = (file) => {
    if (!file) return 'No file selected';

    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File exceeds ${MAX_FILE_SIZE_MB}MB limit. Try compressing or splitting the document.`;
    }

    if (!SUPPORTED_FILE_TYPES[file.type]) {
      return 'Please use PDF, JPG, PNG, or DOCX format.';
    }

    return null;
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(null);
    onFileSelect(selectedFile);
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;

    const validationError = validateFile(droppedFile);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(null);
    onFileSelect(droppedFile);
  };

  // Remove file
  const handleRemove = () => {
    onFileSelect(null);
    setLocalError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayError = localError || error;
  const FileIcon = file ? getFileIcon(file.type) : Upload;

  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-slate-800">Upload your document</h3>
        <p className="text-sm text-slate-500 mt-1">
          Importing: <span className="font-medium text-cyan-600">{typeConfig?.label}</span> checklist
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !file && fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
          ${dragActive ? 'border-cyan-500 bg-cyan-50' : ''}
          ${file ? 'border-green-500 bg-green-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}
          ${displayError ? 'border-red-300 bg-red-50' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.docx"
          onChange={handleFileChange}
          className="hidden"
        />

        {file ? (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mb-4">
              <FileIcon className="h-8 w-8 text-green-600" />
            </div>
            <p className="font-medium text-slate-800 mb-1">{file.name}</p>
            <p className="text-sm text-slate-500">
              {(file.size / 1024 / 1024).toFixed(2)} MB • {SUPPORTED_FILE_TYPES[file.type]?.label || 'File'}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
              Remove
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-4 ${dragActive ? 'bg-cyan-100' : 'bg-slate-100'}`}>
              <Upload className={`h-8 w-8 ${dragActive ? 'text-cyan-600' : 'text-slate-400'}`} />
            </div>
            <p className="font-medium text-slate-700 mb-1">
              {dragActive ? 'Drop file here' : 'Drag and drop or click to browse'}
            </p>
            <p className="text-sm text-slate-500">
              PDF, JPG, PNG, or Word • Max {MAX_FILE_SIZE_MB}MB
            </p>
          </div>
        )}
      </div>

      {/* Error message */}
      {displayError && (
        <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Upload Error</p>
            <p className="text-sm text-red-600 mt-0.5">{displayError}</p>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 p-4 bg-slate-50 rounded-lg">
        <p className="text-sm font-medium text-slate-700 mb-2">For best results:</p>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• Use clear, high-resolution scans or photos</li>
          <li>• Ensure text is legible and not cut off</li>
          <li>• Upload one page at a time for multi-page documents</li>
        </ul>
      </div>
    </div>
  );
};

export default UploadDocumentStep;
